import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ApiClient } from '../api/client';
import type { AppExecutorPolicy, WorkerNode, WorkerToken } from '../types';
import { reportErrorLocal } from '../utils/errors';
import ErrorBanner from './ErrorBanner';

type Props = {
  apiClient: ApiClient;
  appId: string;
  executors: WorkerNode[];
  setError: (message: string | null) => void;
};

const ASSURANCE_CLASSES: Array<{
  id: WorkerToken['assurance_class'];
  label: string;
}> = [
  { id: 'platform_managed', label: 'Platform managed' },
  { id: 'project_managed', label: 'Project managed' },
  { id: 'member_managed', label: 'Member managed' },
  { id: 'unverified', label: 'Unverified' }
];

export function AppExecutorPolicyPanel({ apiClient, appId, executors, setError }: Props) {
  const [policy, setPolicy] = useState<AppExecutorPolicy | null>(null);
  const [mode, setMode] = useState<AppExecutorPolicy['mode']>('inherit');
  const [assuranceClasses, setAssuranceClasses] = useState<
    WorkerToken['assurance_class'][]
  >(['platform_managed', 'project_managed', 'member_managed']);
  const [selectedTokenId, setSelectedTokenId] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const registrations = useMemo(() => {
    const byToken = new Map<string, WorkerNode>();
    for (const executor of executors) {
      // Terminal-only executor responses intentionally redact the registration
      // token. They are not eligible for app workload grants either.
      const tokenId = executor.token_id;
      if (!tokenId) continue;
      const existing = byToken.get(tokenId);
      if (!existing || new Date(executor.last_seen_at) > new Date(existing.last_seen_at)) {
        byToken.set(tokenId, executor);
      }
    }
    return Array.from(byToken.values()).sort((a, b) =>
      (a.name || a.id).localeCompare(b.name || b.id)
    );
  }, [executors]);

  const loadPolicy = useCallback(async () => {
    try {
      const response = await apiClient.get<AppExecutorPolicy>(
        `/api/apps/${encodeURIComponent(appId)}/executor-policy`
      );
      setPolicy(response);
      setMode(response.mode);
      setAssuranceClasses(response.required_assurance_classes);
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to load executor policy', err, setError, setLocalError);
    }
  }, [apiClient, appId, setError]);

  useEffect(() => {
    void loadPolicy();
  }, [loadPolicy]);

  useEffect(() => {
    if (!selectedTokenId && registrations.length) {
      setSelectedTokenId(registrations[0].token_id ?? '');
    }
  }, [registrations, selectedTokenId]);

  async function savePolicy() {
    if (!assuranceClasses.length) {
      setLocalError('Select at least one assurance class.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const response = await apiClient.put<AppExecutorPolicy>(
        `/api/apps/${encodeURIComponent(appId)}/executor-policy`,
        { mode, required_assurance_classes: assuranceClasses }
      );
      setPolicy(response);
      setMessage('Executor policy saved.');
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to save executor policy', err, setError, setLocalError);
    } finally {
      setBusy(false);
    }
  }

  async function grantExecutor() {
    if (!selectedTokenId) return;
    setBusy(true);
    setMessage(null);
    try {
      const response = await apiClient.put<AppExecutorPolicy>(
        `/api/apps/${encodeURIComponent(appId)}/executor-grants`,
        {
          worker_token_id: selectedTokenId,
          allowed_data_classes: ['source', 'prompts', 'artifacts'],
          allowed_workloads: ['conversation']
        }
      );
      setPolicy(response);
      setMessage('Executor registration granted.');
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to grant executor', err, setError, setLocalError);
    } finally {
      setBusy(false);
    }
  }

  async function revokeGrant(grantId: string) {
    setBusy(true);
    setMessage(null);
    try {
      const response = await apiClient.del<AppExecutorPolicy>(
        `/api/apps/${encodeURIComponent(appId)}/executor-grants/${encodeURIComponent(grantId)}`
      );
      setPolicy(response);
      setMessage('Executor registration revoked.');
      setLocalError(null);
    } catch (err) {
      reportErrorLocal('Failed to revoke executor', err, setError, setLocalError);
    } finally {
      setBusy(false);
    }
  }

  const activeGrants = policy?.grants.filter((grant) => !grant.revoked_at) ?? [];

  return (
    <section className="card full stack app-executor-policy" aria-labelledby="executor-policy-title">
      <div className="card-header">
        <div>
          <h3 id="executor-policy-title">Executor policy</h3>
          <p className="status">Restrict conversation workloads to approved registrations.</p>
        </div>
        <span className="badge">{policy?.mode ?? 'loading'}</span>
      </div>
      {localError && <ErrorBanner message={localError} onDismiss={() => setLocalError(null)} />}
      {message && <p className="status status-text--ok">{message}</p>}
      <p className="warning-text">
        Executor administrators can inspect workload data on their hosts. Firecracker isolates
        workloads from each other, not from the executor administrator.
      </p>
      <div className="detail-grid">
        <label>
          Routing mode
          <select value={mode} onChange={(event) => setMode(event.target.value as AppExecutorPolicy['mode'])}>
            <option value="inherit">Inherit platform routing</option>
            <option value="restricted">Restricted allowlist</option>
          </select>
        </label>
        <fieldset>
          <legend>Accepted assurance classes</legend>
          <div className="row">
            {ASSURANCE_CLASSES.map((assurance) => (
              <label key={assurance.id} className="checkbox-row">
                <input
                  type="checkbox"
                  checked={assuranceClasses.includes(assurance.id)}
                  onChange={(event) => {
                    setAssuranceClasses((current) =>
                      event.target.checked
                        ? [...current, assurance.id]
                        : current.filter((value) => value !== assurance.id)
                    );
                  }}
                />
                {assurance.label}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="row">
        <button type="button" onClick={() => void savePolicy()} disabled={busy}>
          {busy ? 'Saving...' : 'Save policy'}
        </button>
      </div>
      {mode === 'restricted' && (
        <>
          <div className="row">
            <select
              aria-label="Executor registration"
              value={selectedTokenId}
              onChange={(event) => setSelectedTokenId(event.target.value)}
            >
              {registrations.length === 0 && <option value="">No registrations available</option>}
              {registrations.map((executor) => (
                <option key={executor.token_id} value={executor.token_id ?? ''}>
                  {executor.name || executor.id} ({executor.assurance_class.replaceAll('_', ' ')})
                </option>
              ))}
            </select>
            <button
              type="button"
              className="secondary"
              onClick={() => void grantExecutor()}
              disabled={busy || !selectedTokenId}
            >
              Grant registration
            </button>
          </div>
          {activeGrants.length ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Executor</th>
                    <th>Assurance</th>
                    <th>Data</th>
                    <th>Workloads</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {activeGrants.map((grant) => (
                    <tr key={grant.id}>
                      <td>
                        {grant.executor_name || grant.executor_id || grant.worker_token_id}
                      </td>
                      <td>{grant.assurance_class.replaceAll('_', ' ')} · {grant.assurance_state}</td>
                      <td>{grant.allowed_data_classes.join(', ')}</td>
                      <td>{grant.allowed_workloads.join(', ')}</td>
                      <td>
                        <button
                          type="button"
                          className="ghost danger"
                          onClick={() => void revokeGrant(grant.id)}
                          disabled={busy}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="status">No active executor registrations are granted.</p>
          )}
        </>
      )}
    </section>
  );
}
